// AKM POC §5.15 — Devam Paneli biçimleyicileri (saf; testlenir).
import { isIsoDay } from '../../utils/attendanceFilterQuery';

const MAX_RANGE_DAYS = 366;

/** "47 sa 20 dk" | "45 dk" | "2 sa" — dakika toplamı insan okuyacağı süreye. */
export function formatDuration(minutes, t) {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0 && m > 0) return t('attendanceDashboard.duration.hm', { h, m });
  if (h > 0) return t('attendanceDashboard.duration.h', { h });
  return t('attendanceDashboard.duration.m', { m });
}

/** Yerel yüzde: tr "%94,2", en "94.2%"; boş değer "—". Değer 0–100 ölçeğinde gelir. */
export function formatPercent(value, lang, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat(lang || 'tr', {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value) / 100);
}

/** Yerel sayı (orantılı rakam; ondalık en fazla `digits`). */
export function formatNumber(value, lang, digits = 0) {
  return Number(value ?? 0).toLocaleString(lang || 'tr', { maximumFractionDigits: digits });
}

// Göstergenin "iyi" yönü: devam oranı yükselince iyi; geç/erken/devamsız/eksik yükselince kötü.
const DELTA_FIELD = {
  rate: 'deltaPoints', late: 'deltaPercent', early: 'deltaPercent', absent: 'deltaDays', undertime: 'deltaPercent',
};

/**
 * Önceki döneme göre değişimin görünümü: { direction:'up'|'down'|'flat', tone:'good'|'bad'|'neutral', value } | null.
 * Değer yoksa (önceki dönemde sıfır olay → backend null) gösterilmez.
 */
export function deltaView(kind, kpi) {
  const field = DELTA_FIELD[kind];
  const value = kpi ? kpi[field] : null;
  if (value === null || value === undefined) return null;
  if (value === 0) return { direction: 'flat', tone: 'neutral', value: 0 };
  const direction = value > 0 ? 'up' : 'down';
  const higherIsGood = kind === 'rate';
  const good = higherIsGood ? value > 0 : value < 0;
  return { direction, tone: good ? 'good' : 'bad', value };
}

const pad = (n) => String(n).padStart(2, '0');
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

function capitalize(text, lang) {
  if (!text) return text;
  return text.charAt(0).toLocaleUpperCase(lang || 'tr') + text.slice(1);
}

/** Son 12 ay (bu ay başta) + "Özel Aralık": [{ id:'2026-10', name:'Ekim 2026' }, …, { id:'custom', name }]. */
export function periodOptions(todayIso, lang, t) {
  const [y, m] = todayIso.split('-').map(Number);
  const fmt = new Intl.DateTimeFormat(lang || 'tr', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const options = [];
  for (let i = 0; i < 12; i += 1) {
    const date = new Date(Date.UTC(y, m - 1 - i, 1));
    const id = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
    options.push({ id, name: capitalize(fmt.format(date), lang) });
  }
  options.push({ id: 'custom', name: t('attendanceDashboard.filters.customRange') });
  return options;
}

/** İki 'yyyy-MM-dd' gün arasındaki gün sayısı (iki uç dahil). */
export function rangeDays(from, to) {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86400000) + 1;
}

/** Özel aralık geçerli mi: iki geçerli gün, from ≤ to, en fazla 366 gün. */
export function isValidCustomRange(from, to) {
  return isIsoDay(from) && isIsoDay(to) && from <= to && rangeDays(from, to) <= MAX_RANGE_DAYS;
}

/**
 * Süzgeç durumu → uç sorgusu { from, to, employeeStatusId, userGroupId, departmentId, locationId }.
 * Özel aralık eksik / ters / 366 günden uzunsa null (istek atılmaz).
 */
export function toQuery(filters, todayIso) {
  const f = filters || {};
  let from;
  let to;
  if (f.periodKey === 'custom') {
    if (!isValidCustomRange(f.customFrom, f.customTo)) return null;
    from = f.customFrom;
    to = f.customTo;
  } else {
    const key = f.periodKey || (todayIso || '').slice(0, 7);
    const match = /^(\d{4})-(\d{2})$/.exec(key);
    if (!match) return null;
    const [y, m] = [Number(match[1]), Number(match[2])];
    from = `${y}-${pad(m)}-01`;
    to = `${y}-${pad(m)}-${pad(lastDayOfMonth(y, m))}`;
  }
  return {
    from,
    to,
    employeeStatusId: f.employeeStatusId || null,
    userGroupId: f.userGroupId || null,
    departmentId: f.departmentId || null,
    locationId: f.locationId || null,
  };
}

/** 'yyyy-MM-dd' → yerel kısa tarih (dd.MM.yyyy tr). */
export function formatIsoDay(iso, lang) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Intl.DateTimeFormat(lang || 'tr', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

/** 'yyyy-MM-dd' → "23 Ekim Perşembe" (ipucu başlığı). */
export function formatDayTitle(iso, lang) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayMonth = new Intl.DateTimeFormat(lang || 'tr', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
  const weekday = new Intl.DateTimeFormat(lang || 'tr', { weekday: 'long', timeZone: 'UTC' }).format(date);
  return `${capitalize(dayMonth, lang)} ${capitalize(weekday, lang)}`;
}

/** 'yyyy-MM-dd' → eksen etiketi "23 Eki". */
export function formatAxisDay(iso, lang) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Intl.DateTimeFormat(lang || 'tr', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)));
}
